import { ComponentProps, ComponentType, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { useDeepEqualMemoValue } from '@components/component-helpers';
import { createCountStore } from '@stores/create-count-store';
import { theme } from '@styles/theme';

export type OverlayOptions = {
  onHide?(): void;
  onBeforeChange?(): boolean | void;
};

type OverlayComponent = ComponentType<any>;

const overlayStore = createCountStore();
let overlayRegistry: {
  options?: OverlayOptions;
  OverlayComponent?: OverlayComponent;
} | null = null;

export const useOverlay = <T extends OverlayComponent>(component: T, options?: OverlayOptions) => {
  const opts = useDeepEqualMemoValue(options);
  return useCallback(
    <K extends ComponentProps<T>>(props?: K, optionsOnShow?: OverlayOptions) => {
      return showOverlay({
        component,
        options: { ...opts, ...optionsOnShow },
        props,
      });
    },
    [component, opts]
  );
};

export const showOverlay = <T extends OverlayComponent, K extends ComponentProps<T>>({
  component: OverlayComponent,
  options,
  props,
}: {
  component: T;
  options?: OverlayOptions;
  props?: K;
}) => {
  overlayRegistry = { options: { ...options, ...props }, OverlayComponent };
  overlayStore.increase();
};

export const hideOverlay = () => {
  overlayStore.reset();
};

const TRANSITION_DURATION = 0.3;
const BACKDROP_OPACITY = 0.5;

const OverlayProvider = () => {
  const { useStore } = overlayStore;
  const count = useStore((s) => s.count);
  const isActive = count > 0;
  const location = useLocation();
  const { options, OverlayComponent } = overlayRegistry ?? {};
  const { onHide } = options ?? {};

  useEffect(() => {
    hideOverlay();
  }, [location]);

  useEffect(() => {
    if (!isActive) {
      onHide?.();
      overlayRegistry = null;
    }
    // eslint-disable-next-line
  }, [isActive]);

  return (
    <AnimatePresence>
      {isActive && (
        <StyledContainer>
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: BACKDROP_OPACITY }}
            transition={{ duration: TRANSITION_DURATION }}
            exit={{ opacity: 0 }}
          />
          <OverlayContent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: TRANSITION_DURATION }}
          >
            {OverlayComponent && <OverlayComponent {...options} onClose={hideOverlay} />}
          </OverlayContent>
        </StyledContainer>
      )}
    </AnimatePresence>
  );
};

const StyledContainer = styled.div({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 9991, // Below modal (9999) but above everything else
});

const Backdrop = styled(motion.div)({
  position: 'fixed',
  backgroundColor: theme.color.purpleTrans40,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  overflow: 'hidden',
  zIndex: -1,
  '.dark &': {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
});

const OverlayContent = styled(motion.div)({
  position: 'relative',
  width: '100%',
  height: '100%',
  pointerEvents: 'none', // Allow clicks through to underlying content
  '> *': {
    pointerEvents: 'auto', // But enable clicks on the overlay content itself
  },
});

export const Overlay = {
  Provider: OverlayProvider,
};

export const useIsOverlayActive = () => overlayStore.useStore((s) => s.count > 0);
